/**
 * Writing one template.
 *
 * The same editing model as the campaign screen: a draft plus the loaded
 * original, and Save sends only the keys that differ. That is not tidiness -
 * `templatePatchSchema` accepts a partial body precisely so a screen that knows
 * about six fields cannot blank the rest by sending them empty.
 *
 * What a template deliberately does NOT hold: an audience, a sending pace, a
 * sender. Copying quiet hours and a daily cap along with the wording is how a
 * careless "start from this one" inherits a limit somebody set for a different
 * list, and whose name is on the letter is a decision per campaign.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { Button, Input, Loader, LocaleCombobox, cn } from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import { hostSiteId, userCanPlatform, type StaffUser } from "../../../../lib/auth";
import { PINNED_HEADER_TOP } from "../../../stickyChrome";
import {
  createTemplate,
  getTemplate,
  patchTemplate,
  type Template,
} from "../../../../lib/marketingTemplates";
import { emptyDesign, type EmailDesign } from "../../../../emailBuilder";
import { LetterEditor } from "../letter/LetterEditor";
import { slugify } from "../lib";
import { Field, Note, Panel } from "../shared";

/** The keys this screen may send. Timestamps and the usage count are the
 *  server's. */
const EDITABLE = ["name", "slug", "description", "locale", "subject", "preheader", "bodyHtml", "bodyText", "design"] as const;

type Draft = Pick<Template, (typeof EDITABLE)[number]>;

const BLANK: Draft = {
  name: "",
  slug: "",
  description: "",
  locale: "et",
  subject: "",
  preheader: "",
  bodyHtml: "",
  bodyText: "",
  design: null,
};

/** The languages a letter template is written in. */
const LETTER_LOCALES = ["et", "en"] as const;

export function TemplateEditor({ user, isNew = false }: { user: StaffUser; isNew?: boolean }) {
  const t = useT();
  const { locale: uiLocale } = useI18n();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const canManage = userCanPlatform(user, "manage:crm.marketing");
  const siteId = hostSiteId(user);

  const [original, setOriginal] = useState<Draft | null>(isNew ? BLANK : null);
  const [draft, setDraft] = useState<Draft | null>(isNew ? BLANK : null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  /** Set when this row is the edited version of a letter that ships with the
   *  module. Not part of the draft: it is not editable, and the editor must not
   *  be able to send it back and re-point somebody's edit at another preset. */
  const [presetId, setPresetId] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) return;
    getTemplate(id)
      .then((res) => {
        const loaded = Object.fromEntries(EDITABLE.map((k) => [k, res.template[k]])) as Draft;
        setOriginal(loaded);
        setDraft(loaded);
        setPresetId(res.template.presetId);
      })
      .catch(() => setNotFound(true));
  }, [id, isNew]);

  const changes = useMemo(() => {
    if (!original || !draft) return {};
    const out: Record<string, unknown> = {};
    for (const k of EDITABLE) {
      // The design is a nested object that has been through jsonb, which does
      // not preserve key order - so it is compared as a rebuilt string, not by
      // identity. Getting this wrong leaves the form permanently dirty.
      const same = k === "design" ? JSON.stringify(original[k]) === JSON.stringify(draft[k]) : original[k] === draft[k];
      if (!same) out[k] = draft[k];
    }
    return out;
  }, [original, draft]);

  const dirty = Object.keys(changes).length > 0;

  // Half-written wording is worth warning about; the browser prompt is crude
  // but it survives a closed tab.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const patch = useCallback((p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d)), []);

  async function save() {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) return;
    setSaving(true);
    try {
      if (isNew) {
        const res = await createTemplate({
          ...draft,
          name,
          slug: draft.slug.trim() || slugify(name) || `template-${Date.now().toString(36)}`,
          description: draft.description || null,
        });
        navigate(`/marketing/templates/${res.template.id}`, { replace: true });
        return;
      }
      const res = await patchTemplate(id, changes);
      const saved = Object.fromEntries(EDITABLE.map((k) => [k, res.template[k]])) as Draft;
      setOriginal(saved);
      setDraft(saved);
      setError(null);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      setError(status === 409 ? t("marketing.error.slugTaken") : t("marketing.error.save"));
    } finally {
      setSaving(false);
    }
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="rounded-2xl border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
          {t("marketing.templates.notFound")}
        </p>
      </div>
    );
  }

  if (!draft) return <Loader />;

  return (
    <div className="space-y-4 pb-10">
      {/* Pins under the app header, not at the top of the window - see
          crm/stickyChrome.ts. */}
      <header
        className={cn(
          PINNED_HEADER_TOP,
          "-mx-1 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card/85 px-4 py-3 backdrop-blur",
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <BackLink />
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold tracking-tight">
              {draft.name.trim() || t(isNew ? "marketing.templates.newTitle" : "marketing.templates.untitled")}
            </h1>
            <p className="truncate text-[11px] text-muted-foreground">
              {draft.subject.trim() || t("marketing.message.noSubject")}
            </p>
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {dirty && !isNew && (
              <Button variant="ghost" size="sm" onClick={() => setDraft(original)}>
                {t("marketing.discard")}
              </Button>
            )}
            <Button size="sm" onClick={() => void save()} disabled={saving || !draft.name.trim() || (!dirty && !isNew)}>
              {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              {t(dirty || isNew ? "marketing.save" : "marketing.saved")}
            </Button>
          </div>
        )}
      </header>

      {error && <Note tone="bad">{error}</Note>}
      {presetId && <Note tone="info">{t("marketing.templates.editingPreset")}</Note>}

      <Panel title={t("marketing.templates.aboutTitle")} description={t("marketing.templates.aboutIntro")}>
        <div className="grid gap-3.5 sm:grid-cols-2">
          <Field label={t("marketing.templates.name")}>
            <Input
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              disabled={!canManage}
              placeholder={t("marketing.templates.namePlaceholder")}
            />
          </Field>
          <Field label={t("marketing.templates.slug")} hint={t("marketing.templates.slugHint")}>
            <Input
              value={draft.slug}
              onChange={(e) => patch({ slug: slugify(e.target.value) })}
              disabled={!canManage}
              placeholder={slugify(draft.name)}
              className="font-mono text-xs"
            />
          </Field>
          <Field label={t("marketing.templates.description")} className="sm:col-span-2">
            <Input
              value={draft.description ?? ""}
              onChange={(e) => patch({ description: e.target.value })}
              disabled={!canManage}
              placeholder={t("marketing.templates.descriptionPlaceholder")}
            />
          </Field>
          <Field label={t("marketing.templates.locale")} hint={t("marketing.templates.localeHint")}>
            <LocaleCombobox
              locales={LETTER_LOCALES}
              displayLocale={uiLocale}
              value={draft.locale}
              onChange={(locale) => patch({ locale })}
              disabled={!canManage}
              aria-label={t("marketing.templates.locale")}
              searchPlaceholder={t("common.localeSearch")}
              emptyLabel={t("common.localeNone")}
            />
          </Field>
        </div>
      </Panel>

      <LetterEditor
        subject={draft.subject}
        preheader={draft.preheader}
        bodyHtml={draft.bodyHtml}
        bodyText={draft.bodyText}
        design={draft.design as EmailDesign | null}
        locale={draft.locale === "en" ? "en" : "et"}
        siteId={siteId}
        disabled={!canManage}
        onChange={(p) => patch(p as Partial<Draft>)}
        onStartDesign={() => patch({ design: emptyDesign() })}
      />
    </div>
  );
}

function BackLink() {
  const t = useT();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate("/marketing/templates")}
      className="inline-flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" /> {t("marketing.templates.back")}
    </button>
  );
}
